import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { REMOVAL_FAILED_TARGET } from '../../../Services/OrderWizard/issues.ts'
import { canReadd, failedRemovals, pendingRemovals, removalHoldingGroup } from '../../../Services/OrderWizard/removal.ts'
import { sampleDisplayName } from '../../../Services/OrderWizard/sampleLabels.ts'
import { groupWriteError } from '../../../Services/OrderWizard/writeState.ts'
import { plural } from '../../../Util/plural.ts'
import { GroupWriteFailure } from './GroupWriteFailure.tsx'
import { RETRY_BUTTON } from './retryStyle.ts'

/** Items the lab removed that the order still holds: until the server lets them go, they stay in sight. */
const RemovalNotes = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const failed = failedRemovals(draft)
  const pending = pendingRemovals(draft)
  const retry = (groupKeys: string[]) => dispatch(wizard.removalRetryRequested({ draftId: draft.id, groupKeys }))
  /* a sample re-added on the files waits for this removal */
  const blocking = new Set(
    Object.values(draft.groups).flatMap((group) => (group.item.status === 'waiting' ? [removalHoldingGroup(draft, group)?.groupKey] : []))
  )
  return (
    <>
      {failed.length > 0 && (
        <div id={REMOVAL_FAILED_TARGET} className="space-y-0.5" data-testid="wizard-removal-failed">
          <ul className="space-y-0.5">
            {failed.map((removal) => (
              <li key={removal.groupKey} className="flex min-w-0 items-center gap-1" data-testid="wizard-removal-failed-item">
                <span className="shrink-0 font-medium text-slate-800">{removal.name}:</span>
                <span className="flex min-w-0 items-center gap-1 text-error" title={removal.error}>
                  <ExclamationTriangleIcon className="h-3 w-3 shrink-0" />
                  <span className="truncate">removal failed{removal.error ? ` (${removal.error})` : ''}</span>
                </span>
                <button type="button" className={RETRY_BUTTON} data-testid="wizard-removal-retry" onClick={() => retry([removal.groupKey])}>
                  {blocking.has(removal.groupKey) ? 'Retry removing the old sample' : 'Try again'}
                </button>
              </li>
            ))}
          </ul>
          {failed.length > 1 && (
            <button
              type="button"
              className={`${RETRY_BUTTON} px-0`}
              data-testid="wizard-removal-retry-all"
              onClick={() => retry(failed.map((removal) => removal.groupKey))}
            >
              Try all {failed.length} again
            </button>
          )}
        </div>
      )}
      {pending.length > 0 && (
        <p className="text-slate-500" data-testid="wizard-removal-pending">
          Removing {plural(pending.length, 'item')} from the order…
        </p>
      )}
    </>
  )
}

/** What the lab removed, each with the way back: "Re-add" from the files the order kept, else dropping the files again. */
const RemovedByLab = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const shown = Object.entries(draft.removedGroups ?? {}).filter(([, removed]) => !removed.dismissed)
  if (shown.length === 0) {
    return null
  }
  const readdable = shown.filter(([, removed]) => canReadd(draft, removed)).map(([key]) => key)
  const readd = (groupKeys: string[]) => dispatch(wizard.itemsReadded({ draftId: draft.id, groupKeys }))
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 text-slate-600" data-testid="wizard-removed-by-lab">
      <span>Removed:</span>
      {shown.map(([key, removed], index) => (
        <span key={key} className="inline-flex items-center gap-0.5" data-testid="wizard-removed-item">
          <span className="font-medium text-slate-800">{removed.name}</span>
          {readdable.includes(key) ? (
            <button type="button" className={`${RETRY_BUTTON} px-0.5`} data-testid="wizard-readd" onClick={() => readd([key])}>
              Re-add
            </button>
          ) : (
            <span className="text-slate-500">(drop its files again to add it back)</span>
          )}
          {index < shown.length - 1 && <span aria-hidden>·</span>}
        </span>
      ))}
      {readdable.length > 1 && (
        <button type="button" className={`${RETRY_BUTTON} px-0`} data-testid="wizard-readd-all" onClick={() => readd(readdable)}>
          Re-add all {readdable.length}
        </button>
      )}
      <button
        type="button"
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Dismiss"
        data-testid="wizard-removed-dismiss"
        onClick={() => dispatch(wizard.removedGroupsDismissed({ draftId: draft.id }))}
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </div>
  )
}

/** Items deleted in the classic view and items the wizard could not write or remove, which the board does not spell out. */
export const ItemNotes = ({ draft }: { draft: DraftState }) => {
  const groups = draft.fileOrder.map((key) => draft.groups[key]).filter((group) => !!group)
  const removed = groups.filter((group) => group.item.status === 'removed')
  const failed = groups.filter((group) => group.item.status !== 'removed' && !!groupWriteError(group))
  if (removed.length === 0 && failed.length === 0 && !draft.removals && !draft.removedGroups) {
    return null
  }
  return (
    <div className="space-y-1 text-xs">
      <RemovedByLab draft={draft} />
      <RemovalNotes draft={draft} />
      {failed.length > 0 && (
        <ul className="space-y-0.5" data-testid="wizard-failed-items">
          {failed.map((group) => (
            <li key={group.key} className="flex min-w-0 items-center gap-1">
              <span className="shrink-0 font-medium text-slate-800">{sampleDisplayName(draft, group.key)}:</span>
              <GroupWriteFailure draft={draft} group={group} />
            </li>
          ))}
        </ul>
      )}
      {removed.length > 0 && (
        <p className="text-slate-500" data-testid="wizard-removed-items">
          Removed in the classic view: {removed.map((group) => sampleDisplayName(draft, group.key)).join(', ')}
        </p>
      )}
    </div>
  )
}
